'use client';
import React, { useState, useRef, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { fetchAllPaddles, type Paddle } from '@/lib/services/paddleService';
import { useComparison } from '@/contexts/ComparisonContext';

export default function Hero() {
  const router = useRouter();
  const { selectedItems, addItem, removeItem } = useComparison();
  const [query, setQuery] = useState('');
  const [allPaddles, setAllPaddles] = useState<Paddle[]>([]);
  const [results, setResults] = useState<Paddle[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [mounted, setMounted] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMounted(true);
    fetchAllPaddles().then(data => setAllPaddles(data));
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = (q: string) => {
    setQuery(q);
    if (q.length > 0) {
      const lowerQ = q.toLowerCase();
      const words = lowerQ.split(/\s+/).filter(Boolean);
      const filtered = allPaddles
        .filter(p => {
          const combined = `${p.brand} ${p.name}`.toLowerCase();
          return (
            !selectedItems.find(s => s.id === p.id) &&
            words.every(word => combined.includes(word))
          );
        })
        .slice(0, 50);
      setResults(filtered);
      setShowDropdown(true);
    } else {
      setResults([]);
      setShowDropdown(false);
    }
  };

  const handleSelect = (paddle: Paddle) => {
    addItem(paddle);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const handleRemove = (id: string) => {
    removeItem(id);
  };

  const handleCompare = () => {
    if (selectedItems.length < 2) return;
    const ids = selectedItems.map(p => p.slug).join('-vs-');
    router.push(`/compare/${ids}`);
  };

  const scrollDown = () => {
    window.scrollTo({ top: window.innerHeight, behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex flex-col"
      style={{ background: '#0D0D0D' }}
    >
      {/* Deep black base */}
      <div className="hero-dark-bg" />

      {/* Burgundy / cherry red glow atmosphere */}
      <div className="hero-burgundy-glow" />

      {/* Subtle grid lines */}
      <div className="hero-grid-overlay" />

      {/* Top edge vignette */}
      <div
        className="absolute top-0 left-0 right-0 pointer-events-none"
        style={{
          height: '200px',
          background: 'linear-gradient(to bottom, rgba(0,0,0,0.6) 0%, transparent 100%)',
        }}
      />

      {/* Bottom fade to white */}
      <div
        className="absolute bottom-0 left-0 right-0 pointer-events-none"
        style={{
          height: '120px',
          background: 'linear-gradient(to bottom, transparent 0%, rgba(13,13,13,0.95) 100%)',
        }}
      />

      {/* Main content */}
      <div className="flex-1 flex flex-col items-center justify-center text-center px-4 pt-28 pb-24 relative z-10">

        {/* Eyebrow label */}
        <div
          className="inline-flex items-center gap-2 mb-10 px-4 py-1.5 rounded-full"
          style={{
            background: 'rgba(217,0,18,0.12)',
            border: '1px solid rgba(217,0,18,0.3)',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: '#D90012',
              display: 'inline-block',
              boxShadow: '0 0 6px rgba(217,0,18,0.6)',
            }}
          />
          <span
            className="text-xs font-bold uppercase tracking-widest"
            style={{ color: 'rgba(255,100,100,0.9)', letterSpacing: '0.13em' }}
          >
            2026 Pickleball Intelligence Platform
          </span>
        </div>

        {/* Headline */}
        <h1
          className="font-black leading-none mb-6"
          style={{
            fontSize: 'clamp(46px, 8vw, 96px)',
            letterSpacing: '-0.04em',
            lineHeight: 0.93,
            color: '#FFFFFF',
          }}
        >
          compare{' '}
          <br className="hidden sm:block" />
          <span className="hero-headline-accent">
            every paddle.
          </span>
        </h1>

        {/* Subtitle */}
        <p
          className="mb-12 max-w-lg leading-relaxed"
          style={{
            color: 'rgba(255,255,255,0.55)',
            fontSize: 'clamp(15px, 1.8vw, 18px)',
            fontWeight: 400,
            letterSpacing: '-0.01em',
          }}
        >
          Expert data on{' '}
          <a
            href="/categories/carbon-fiber"
            style={{ color: 'rgba(255,255,255,0.82)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.22)', paddingBottom: '1px', transition: 'color 0.2s, border-color 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF4444'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(217,0,18,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.82)'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(255,255,255,0.22)'; }}
          >carbon fiber</a>,{' '}
          <a
            href="/categories/fiberglass"
            style={{ color: 'rgba(255,255,255,0.82)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.22)', paddingBottom: '1px', transition: 'color 0.2s, border-color 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF4444'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(217,0,18,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.82)'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(255,255,255,0.22)'; }}
          >fiberglass</a>,{' '}
          <a
            href="/categories/tournament"
            style={{ color: 'rgba(255,255,255,0.82)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.22)', paddingBottom: '1px', transition: 'color 0.2s, border-color 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF4444'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(217,0,18,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.82)'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(255,255,255,0.22)'; }}
          >tournament</a>, and{' '}
          <a
            href="/categories"
            style={{ color: 'rgba(255,255,255,0.82)', textDecoration: 'none', borderBottom: '1px solid rgba(255,255,255,0.22)', paddingBottom: '1px', transition: 'color 0.2s, border-color 0.2s' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = '#FF4444'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(217,0,18,0.5)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.82)'; (e.currentTarget as HTMLElement).style.borderBottomColor = 'rgba(255,255,255,0.22)'; }}
          >more</a>.
        </p>

        {/* Comparison Builder */}
        <div className="w-full max-w-lg">
          <div className="flex flex-col">
            {/* Selected paddles */}
            {selectedItems.map((item, i) => (
              <div key={item.id} className="flex items-center mb-3">
                <div className="flex items-center justify-end mr-4" style={{ width: 30 }}>
                  {i > 0 && (
                    <span
                      className="text-xs font-black select-none px-1.5 py-0.5 rounded-md"
                      style={{ color: '#D90012', letterSpacing: '0.06em', background: 'rgba(217,0,18,0.15)', border: '1px solid rgba(217,0,18,0.3)' }}
                    >vs</span>
                  )}
                </div>
                <div
                  className="hero-paddle-pill flex-1 flex items-center justify-between px-5 py-4"
                >
                  <span className="font-semibold text-sm truncate pr-4" style={{ color: '#FFFFFF', letterSpacing: '-0.01em' }}>
                    {item.brand} {item.name}
                  </span>
                  <button
                    onClick={() => handleRemove(item.id)}
                    className="w-6 h-6 rounded-full flex items-center justify-center transition-all flex-shrink-0"
                    style={{ color: 'rgba(255,255,255,0.32)' }}
                    onMouseEnter={e => {
                      (e.currentTarget as HTMLElement).style.color = 'rgba(255,80,80,0.9)';
                      (e.currentTarget as HTMLElement).style.background = 'rgba(217,0,18,0.18)';
                    }}
                    onMouseLeave={e => {
                      (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.32)';
                      (e.currentTarget as HTMLElement).style.background = 'transparent';
                    }}
                    aria-label={`Remove ${item.name}`}
                  >
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}

            {/* VS label before input row */}
            <div className="flex items-center">
              <div className="flex items-center justify-end mr-4" style={{ width: 30 }}>
                {selectedItems.length > 0 && (
                  <span
                    className="text-xs font-black select-none px-1.5 py-0.5 rounded-md"
                    style={{ color: '#D90012', letterSpacing: '0.06em', background: 'rgba(217,0,18,0.15)', border: '1px solid rgba(217,0,18,0.3)' }}
                  >vs</span>
                )}
              </div>

              <div ref={dropdownRef} className="flex-1 flex items-center gap-3 relative">
                <div className="flex-1 relative">
                  <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder={selectedItems.length === 0 ? 'Search a paddle to compare...' : 'Add another paddle...'}
                    className="hero-input w-full"
                  />

                  {/* Dropdown */}
                  {showDropdown && results.length > 0 && (
                    <div
                      className="absolute top-full left-0 right-0 mt-2 z-[200]"
                      style={{
                        background: '#FFFFFF',
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: '16px',
                        boxShadow: '0 20px 56px rgba(0,0,0,0.2)',
                        maxHeight: '400px',
                        overflowY: 'auto',
                      }}
                    >
                      {results.map((p, i) => (
                        <button
                          key={p.id}
                          onClick={() => handleSelect(p)}
                          className="w-full flex items-center gap-3 px-4 py-3 text-left transition-all"
                          style={{ borderBottom: i < results.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none' }}
                          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(217,0,18,0.03)')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <div
                            className="w-8 h-8 rounded-xl overflow-hidden flex-shrink-0"
                            style={{ background: 'rgba(0,0,0,0.04)', border: '1px solid rgba(0,0,0,0.07)' }}
                          >
                            <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = '/assets/images/no_image.png'; }} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate" style={{ color: '#0D0D0D', letterSpacing: '-0.01em' }}>{p.brand} {p.name}</div>
                            <div className="text-xs mt-0.5" style={{ color: 'rgba(13,13,13,0.4)' }}>{p.priceFormatted}</div>
                          </div>
                          <div className="text-xs font-bold flex-shrink-0 tabular-nums" style={{ color: '#D90012' }}>{isNaN(p.score) || !isFinite(p.score) ? 70 : p.score} pts</div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Compare button */}
                <button
                  onClick={handleCompare}
                  disabled={selectedItems.length < 2}
                  className="hero-compare-btn flex-shrink-0"
                  style={{ minWidth: 104 }}
                >
                  Compare
                </button>
              </div>
            </div>
          </div>

          {/* Helper text */}
          {selectedItems.length < 2 && (
            <p
              className="mt-4 text-center text-xs"
              style={{ color: 'rgba(255,255,255,0.26)', letterSpacing: '0.02em' }}
            >
              {selectedItems.length === 0
                ? 'Add 2 or more paddles to start comparing' : 'Add one more paddle to compare'}
            </p>
          )}

          {/* Compare CTA when ready */}
          {selectedItems.length >= 2 && (
            <button
              onClick={handleCompare}
              className="mt-5 w-full font-black py-4 rounded-2xl transition-all uppercase tracking-widest text-sm"
              style={{
                background: '#D90012',
                color: '#fff',
                letterSpacing: '0.1em',
                boxShadow: '0 6px 24px rgba(217,0,18,0.44)',
              }}
              onMouseEnter={e => {
                (e.currentTarget as HTMLElement).style.background = '#A80010';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 10px 32px rgba(217,0,18,0.54)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={e => {
                (e.currentTarget as HTMLElement).style.background = '#D90012';
                (e.currentTarget as HTMLElement).style.boxShadow = '0 6px 24px rgba(217,0,18,0.44)';
                (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
              }}
            >
              Compare {selectedItems.length} Paddles →
            </button>
          )}
        </div>

        {/* Trust indicators */}
        <div className="flex flex-wrap items-center justify-center gap-7 mt-14">
          {[
            '451 Paddles',
            '57+ Brands',
            'Lab-Tested Data',
            'No Sponsorships',
          ].map(item => (
            <span key={item} className="trust-indicator">{item}</span>
          ))}
        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollDown}
        className="scroll-indicator"
        aria-label="Scroll down"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <path d="M12 5v14M5 12l7 7 7-7"/>
        </svg>
      </button>
    </section>
  );
}
